/*
 * Copyright Camunda Services GmbH and/or licensed to Camunda Services GmbH under
 * one or more contributor license agreements. See the NOTICE file distributed
 * with this work for additional information regarding copyright ownership.
 * Licensed under the Camunda License 1.0. You may not use this file
 * except in compliance with the Camunda License 1.0.
 */

import { useState, useEffect, useCallback, useRef } from 'react';

import { constrainToViewport } from 'features/properties-panel-popup/util';

export const useDraggableConstrainedPosition = (
  wrapperRef,
  handleRef,
  initialPosition = { x: 0, y: 0 },
  minimumVisibility = undefined
) => {
  const [position, setPosition] = useState(initialPosition);
  const [isDragging, setIsDragging] = useState(false);
  const sizeRef = useRef({ width: 0, height: 0 });

  const setConstrainedPosition = useCallback(
    (newPosition) => {
      if (wrapperRef.current) {
        const { width, height } = wrapperRef.current.getBoundingClientRect();
        sizeRef.current = { width, height };
      }
      const constrainedPosition = constrainToViewport(newPosition, sizeRef.current, minimumVisibility);
      setPosition(constrainedPosition);
    },
    [wrapperRef, minimumVisibility]
  );

  const handleMouseDown = useCallback(
    (e) => {
      if (!handleRef.current?.contains(e.target)) {
        return;
      }

      if (wrapperRef.current) {
        const { width, height } = wrapperRef.current.getBoundingClientRect();
        sizeRef.current = { width, height };
      }

      setIsDragging(true);
      const startX = e.clientX - position.x;
      const startY = e.clientY - position.y;

      const handleMouseMove = (e) => {
        const newPosition = {
          x: e.clientX - startX,
          y: e.clientY - startY
        };

        setConstrainedPosition(newPosition);
      };

      const handleMouseUp = () => {
        setIsDragging(false);
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    },
    [handleRef, position, setConstrainedPosition, wrapperRef]
  );

  useEffect(() => {
    const element = wrapperRef.current;
    if (!element) {
      return;
    }

    element.addEventListener('mousedown', handleMouseDown);
    return () => element.removeEventListener('mousedown', handleMouseDown);
  }, [wrapperRef, handleMouseDown]);

  useEffect(() => {
    const handleResize = () => {
      setConstrainedPosition(position);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [position, setConstrainedPosition]);

  return { position, isDragging };
};
